import { useEffect, useMemo, useState } from "react";
import { api, errorMessage } from "../../api";
import type { ExternalIdentity } from "../../api/types";
import { ConnectorBadge, EmptyState, ErrorBox, Spinner } from "../../components/bits";
import { isAdmin, loadMembers, toastError } from "../../store/app";
import { useActors, useApp } from "../../store/hooks";

export function PeoplePage() {
  const orgId = useApp((s) => s.orgId);
  const actors = useActors();
  const [identities, setIdentities] = useState<ExternalIdentity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "unmatched">("all");
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    if (!orgId) return;
    setError(null);
    api
      .externalIdentities(orgId)
      .then((r) => setIdentities(r.identities))
      .catch((e) => setError(errorMessage(e)));
  };
  useEffect(load, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(
    () => (identities ?? []).filter((i) => filter === "all" || !i.actor_id),
    [identities, filter],
  );

  if (!isAdmin()) return <p className="muted">Only administrators can manage identities.</p>;
  const unmatched = (identities ?? []).filter((i) => !i.actor_id).length;

  const replace = (updated: ExternalIdentity) =>
    setIdentities((list) => list?.map((x) => (x.id === updated.id ? updated : x)) ?? null);

  return (
    <section aria-labelledby="people-title">
      <div className="section-head">
        <h2 id="people-title">
          External identities {unmatched > 0 && <span className="chip warn-chip">{unmatched} unmatched</span>}
        </h2>
        <label className="inline-field">
          <span>Show</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value as "all" | "unmatched")}>
            <option value="all">All</option>
            <option value="unmatched">Unmatched only</option>
          </select>
        </label>
      </div>
      <p className="muted small">
        Users reported by connected tools. Link each one to a person here, or create a ghost person for people without
        an account.
      </p>
      {error && <ErrorBox error={error} onRetry={load} />}
      {identities === null && !error ? (
        <Spinner />
      ) : shown.length === 0 ? (
        <EmptyState title="No identities" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Tool</th>
                <th>Remote user</th>
                <th>Email</th>
                <th>Linked person</th>
                <th>Match</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((i) => (
                <tr key={i.id}>
                  <td>
                    <ConnectorBadge type={i.connector_type} />
                    {i.remote_tenant && <div className="muted small">{i.remote_tenant}</div>}
                  </td>
                  <td>
                    {i.display_name ?? i.handle ?? i.remote_user_id}
                    {i.handle && i.display_name && <div className="muted small">@{i.handle}</div>}
                  </td>
                  <td className="small">{i.email ?? "—"}</td>
                  <td>
                    <select
                      aria-label={`Linked person for ${i.display_name ?? i.remote_user_id}`}
                      value={i.actor_id ?? ""}
                      disabled={busy === i.id}
                      onChange={async (e) => {
                        setBusy(i.id);
                        try {
                          replace(await api.patchExternalIdentity(i.id, e.target.value || null));
                        } catch (err) {
                          toastError(err, "Update failed");
                        } finally {
                          setBusy(null);
                        }
                      }}
                    >
                      <option value="">— not linked —</option>
                      {actors.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.display_name}
                          {a.kind !== "member" ? ` (${a.kind})` : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className={`chip match-${i.match_method}`}>{i.match_method.replace(/_/g, " ")}</span>
                  </td>
                  <td>
                    {!i.actor_id && (
                      <button
                        type="button"
                        className="btn small"
                        disabled={busy === i.id}
                        onClick={async () => {
                          setBusy(i.id);
                          try {
                            replace(await api.ghostExternalIdentity(i.id));
                            await loadMembers(true);
                          } catch (err) {
                            toastError(err, "Could not create ghost");
                          } finally {
                            setBusy(null);
                          }
                        }}
                      >
                        Create ghost
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
